import { useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  useNodesInitialized,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { computeLayout } from './layout';
import { toReactFlow } from './rf';
import { vertexNeedsExpand } from './model';
import GraphVertexNode from './nodes';
import GraphFloatingEdge from './edges';

// Node/edge type maps defined outside the component to preserve referential
// identity across renders (React Flow uses these as a stable lookup table).
const NODE_TYPES = { graphVertex: GraphVertexNode };
const EDGE_TYPES = { floating: GraphFloatingEdge };

/**
 * GraphCanvasInner — the controlled React Flow canvas. Must live inside a ReactFlowProvider so it can use
 * useReactFlow()/useNodesInitialized() to run layout against the REAL measured node sizes (node labels vary in
 * width, so a fixed size produces broken/overlapping layouts).
 */
function GraphCanvasInner({
  elements, layoutName, subsetMode, neighborCursors, canEdit,
  onExpand, onConnect, onNodesDelete, onEdgesDelete, onSelect, panelOpen,
}) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const { getNodes, fitView } = useReactFlow();
  const nodesInitialized = useNodesInitialized();

  // Refs so the once-attached keydown listener always sees the latest state/handlers without re-binding.
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const onNodesDeleteRef = useRef(onNodesDelete);
  const onEdgesDeleteRef = useRef(onEdgesDelete);
  const canEditRef = useRef(canEdit);
  nodesRef.current = nodes;
  edgesRef.current = edges;
  onNodesDeleteRef.current = onNodesDelete;
  onEdgesDeleteRef.current = onEdgesDelete;
  canEditRef.current = canEdit;

  // Compute layout whenever the elements, the chosen layout, or the measured state change. Uses the REAL
  // measured node sizes from the live store (falling back to defaults before the first measurement) so wide
  // nodes are spaced correctly. Re-running on `nodesInitialized` gives a corrected pass once sizes are known;
  // re-running on `layoutName` is what makes the layout dropdown take effect.
  useEffect(() => {
    let cancelled = false;
    const measured = getNodes();
    const sizeById = {};
    for (const n of measured) {
      if (n.measured && n.measured.width) {
        sizeById[n.id] = { width: n.measured.width, height: n.measured.height };
      }
    }
    computeLayout(elements, layoutName, sizeById).then(positions => {
      if (cancelled) return;
      // Preserve positions for ids the layout didn't return (defensive); fresh layout positions win.
      const prevPos = {};
      for (const n of nodesRef.current) prevPos[n.id] = n.position;
      const { nodes: rfNodes, edges: rfEdges } = toReactFlow(elements, { ...prevPos, ...positions });
      // Carry over each node's measured size from the live store so React Flow keeps treating them as measured.
      // Without this, freshly built node objects look unmeasured → RF re-measures → useNodesInitialized flips →
      // this effect re-runs → rebuilds unmeasured nodes again → infinite flicker.
      const measuredById = {};
      for (const n of getNodes()) {
        if (n.measured) measuredById[n.id] = n.measured;
      }
      // In subset mode, mark frontier vertices (still expandable) so they read as tinted and carry an
      // expand affordance; fully-loaded vertices stay plain. `data.onExpand` powers the in-node button.
      setNodes(rfNodes.map(n => {
        const canExpand = !!subsetMode && vertexNeedsExpand(neighborCursors, n.id);
        const next = {
          ...n,
          className: canExpand ? 'graph-vertex-frontier' : undefined,
          data: { ...n.data, canExpand, onExpand: () => onExpand && onExpand(n.id) },
        };
        return measuredById[n.id] ? { ...next, measured: measuredById[n.id] } : next;
      }));
      setEdges(rfEdges);
      setTimeout(() => { if (!cancelled) fitView({ padding: 0.2 }); }, 0);
    });
    return () => { cancelled = true; };
  }, [elements, layoutName, nodesInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Own the Delete/Backspace key. The playground workspace (an outer React Flow that positions widgets) and a
  // document-level handler would otherwise delete the whole widget. We listen at document capture so we run
  // first: when THIS graph has a selected element (and we're not typing in a field), delete it and stop the
  // event so it never reaches those outer handlers. When nothing is selected we let it through, so an empty
  // Delete can still remove the widget.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      // Read-only viewers cannot delete elements — let the key bubble (and never delete) for them.
      if (!canEditRef.current) return;
      // Never steal the key while typing in a field (details-panel props, create modal, etc.).
      const target = event.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (target && target.isContentEditable)) return;
      const selectedNodes = nodesRef.current.filter(n => n.selected);
      const selectedEdges = edgesRef.current.filter(e => e.selected);
      if (selectedNodes.length === 0 && selectedEdges.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      if (selectedNodes.length > 0 && onNodesDeleteRef.current) onNodesDeleteRef.current(selectedNodes);
      if (selectedEdges.length > 0 && onEdgesDeleteRef.current) onEdgesDeleteRef.current(selectedEdges);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const onNodesChange = (changes) => {
    setNodes(prev => applyNodeChanges(changes, prev));
  };

  const onEdgesChange = (changes) => {
    setEdges(prev => applyEdgeChanges(changes, prev));
  };

  const handleConnect = (connection) => {
    if ( onConnect ) onConnect(connection);
  };

  const onNodeDoubleClick = (_event, node) => {
    if ( onExpand && node && node.id ) {
      onExpand(node.id);
    }
  };

  const handleNodeClick = (_event, node) => {
    if ( onSelect && node && node.data && node.data.element ) {
      onSelect(node.data.element);
    }
  };

  const handleEdgeClick = (_event, edge) => {
    if ( onSelect && edge && edge.data && edge.data.element ) {
      onSelect(edge.data.element);
    }
  };

  const handlePaneClick = () => {
    if ( onSelect ) onSelect(null);
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Filled arrowhead markers, rendered as a sibling BEFORE <ReactFlow> with graph-specific ids — the
          known-good placement the mission graph uses. (A shared marker id mounted inside this nested
          canvas did not resolve, so the edges rendered without arrowheads.) */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
        <defs>
          {[
            { id: 'graph-edge-arrow', color: 'var(--bh-edge)' },
            { id: 'graph-edge-arrow-selected', color: 'var(--bh-edge-selected)' },
          ].map(m => (
            <marker
              key={m.id}
              id={m.id}
              markerWidth="11.2"
              markerHeight="5.6"
              viewBox="0 0 16 8"
              orient="auto"
              refX="16"
              refY="4"
              markerUnits="userSpaceOnUse"
            >
              {/* `style`, not the `fill` presentation attribute: var() in a presentation attribute
                  is unsupported on ios_saf 11, still a production target, where it would paint the
                  arrowhead black. Same reasoning as flowkit/flowEdge.js. */}
              <path d="M0,0 L16,4 L0,8 Z" style={{ fill: m.color }} />
            </marker>
          ))}
        </defs>
      </svg>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        connectionMode="loose"
        nodesConnectable={!!canEdit}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={handleConnect}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onPaneClick={handlePaneClick}
        deleteKeyCode={null}
        fitView
      >
        {!panelOpen && <Controls showInteractive={false} />}
        <Background id="-graph-widget-bg" variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>
    </div>
  );
}

/**
 * GraphCanvas — wraps the inner canvas in a ReactFlowProvider so the layout pass can read measured node sizes.
 */
function GraphCanvas(props) {
  return (
    <ReactFlowProvider>
      <GraphCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default GraphCanvas;
